import React, { useMemo } from 'react';
import { F90H } from '../../../services/p930hApi';
import { CalendarMode } from '../hooks/useCalendarQuery';
import { formatDate, getDayName, getEventTypeLabel } from '../helpers';

interface Tab2Props {
  year: number;
  setYear: (v: number) => void;
  month: number;
  setMonth: (v: number) => void;
  calMode: CalendarMode;
  setCalMode: (v: CalendarMode) => void;
  events: F90H[];
  queryLoading: boolean;
  queried: boolean;
  onQuery: () => void;
}

const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const Tab2CalendarView: React.FC<Tab2Props> = React.memo(({
  year, setYear, month, setMonth, calMode, setCalMode,
  events, queryLoading, queried, onQuery,
}) => {
  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = new Map<string, F90H[]>();
    for (const ev of events) {
      const key = (ev.f90h_01 || '').substring(0, 10);
      if (!key) continue;
      const arr = map.get(key) || [];
      arr.push(ev);
      map.set(key, arr);
    }
    return map;
  }, [events]);

  // Build calendar grid for single month mode
  const calendarGrid = useMemo(() => {
    if (calMode !== 1 || !queried) return null;
    const firstDay = new Date(year, month - 1, 1);
    const startOffset = firstDay.getDay(); // Sunday=0
    const daysInMonth = new Date(year, month, 0).getDate();
    const weeks: (number | null)[][] = [];
    let week: (number | null)[] = [];
    for (let i = 0; i < startOffset; i++) week.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      week.push(d);
      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      weeks.push(week);
    }
    return weeks;
  }, [year, month, calMode, queried]);

  const getDateKey = (day: number): string => {
    const m = String(month).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  return (
    <div>
      {/* Query Controls */}
      <div className="calendar-query-bar">
        <div className="field-group">
          <label>列印格式:</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                value={1}
                checked={calMode === 1}
                onChange={() => setCalMode(1)}
              />
              單月行事曆
            </label>
            <label>
              <input
                type="radio"
                value={2}
                checked={calMode === 2}
                onChange={() => setCalMode(2)}
              />
              跨月行事曆
            </label>
          </div>
        </div>

        <div className="field-group">
          <label>年度月份:</label>
          <input
            type="number"
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            style={{ width: 90 }}
            min={1900}
            max={9999}
          />
          <span>年</span>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ width: 70 }}>
            {MONTHS.map(m => (
              <option key={m} value={Number(m)}>{m}</option>
            ))}
          </select>
          <span>月</span>
        </div>

        <button
          className="btn btn-primary"
          onClick={onQuery}
          disabled={queryLoading}
        >
          {queryLoading ? '查詢中...' : '查詢'}
        </button>
      </div>

      {/* Results */}
      {queried && (
        <div style={{ marginTop: 12 }}>
          {calMode === 1 && calendarGrid ? (
            /* Calendar Grid View */
            <div className="calendar-grid-wrapper">
              <h3 style={{ textAlign: 'center', margin: '0 0 12px', fontSize: 16 }}>
                {year} 年 {String(month).padStart(2, '0')} 月 行事曆
              </h3>
              <table className="calendar-grid">
                <thead>
                  <tr>
                    {['日', '一', '二', '三', '四', '五', '六'].map(d => (
                      <th key={d} className={d === '日' || d === '六' ? 'weekend' : ''}>{d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {calendarGrid.map((week, wi) => (
                    <tr key={wi}>
                      {week.map((day, di) => {
                        if (day === null) return <td key={di} className="empty" />;
                        const dateKey = getDateKey(day);
                        const dayEvents = eventsByDate.get(dateKey) || [];
                        const isWeekend = di === 0 || di === 6;
                        return (
                          <td key={di} className={`day-cell ${isWeekend ? 'weekend' : ''} ${dayEvents.length > 0 ? 'has-event' : ''}`}>
                            <div className="day-number">{day}</div>
                            {dayEvents.map((ev, ei) => (
                              <div key={ei} className="event-tag" title={ev.f90h_14}>
                                {ev.f90h_14}
                              </div>
                            ))}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* List View for cross-month or fallback */
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>日期</th>
                    <th>星期</th>
                    <th>事件代碼</th>
                    <th>類型</th>
                    <th>簡碼</th>
                    <th>事件名稱</th>
                    <th>詳細說明</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                        查無資料
                      </td>
                    </tr>
                  ) : events.map((ev, i) => (
                    <tr key={i}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(ev.f90h_01)}</td>
                      <td>{getDayName(ev.f90h_01)}</td>
                      <td>{ev.f90h_11}</td>
                      <td>{getEventTypeLabel(ev.f90h_12)}</td>
                      <td>{ev.f90h_13}</td>
                      <td>{ev.f90h_14}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ev.f90h_15}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '8px 16px', fontSize: 12, color: '#999' }}>
                共 {events.length} 筆事件
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

Tab2CalendarView.displayName = 'Tab2CalendarView';
export default Tab2CalendarView;
